export const TONE_RULES = {
    // 分区色调分析规则
    // 基本色调规则
    PARTITION_MAIN_TONE: {
        condition: (structuredConclusions) => {
            const rules = [];
            const { tone } = structuredConclusions.mainTone;

            if (tone) {
                const toneRule = tone.toUpperCase();
                rules.push(`tone${toneRule}`);
            }

            return rules;
        }
    },

    // 色调倾向强度
    PARTITION_TONE_INTENSITY: {
        condition: (structuredConclusions) => {
            const rules = [];
            const intensity = structuredConclusions.mainTone.intensity;

            if (intensity === "STRONG") {
                rules.push("intensityStrong");
            } else if (intensity === "MODERATE") {
                rules.push("intensityModerate");
            } else if (intensity === "WEAK") {
                rules.push("intensityWeak");
            }

            return rules;
        }
    },

    // 色调稳定性规则
    PARTITION_TONE_STABILITY: {
        condition: (analysis) => {
            const rules = [];
            const statistics = analysis?.a?.statistics;

            if (!statistics) {
                return rules;
            }

            const modeMedianDiff = Math.abs(statistics.mode - statistics.median);
            const modeMeanDiff = Math.abs(statistics.mode - statistics.mean);

            if (modeMedianDiff < 5 && modeMeanDiff < 5) {
                rules.push("toneStable");
            } else if (modeMedianDiff >= 5 || modeMeanDiff >= 5) {
                if (statistics.mean > statistics.mode && statistics.skewness > 0) {
                    rules.push("localWarmHighlight");
                } else if (statistics.mean < statistics.mode && statistics.skewness < 0) {
                    rules.push("localCoolShadow");
                } else {
                    rules.push("localToneDiversity");
                }
            }

            return rules;
        }
    },

    // 色调范围规则
    PARTITION_TONE_RANGE: {
        condition: (structuredConclusions) => {
            const rules = [];
            const { distribution } = structuredConclusions.mainTone;

            if (distribution) {
                const distributionRule = `toneRange${distribution.toLowerCase().charAt(0).toUpperCase() + distribution.toLowerCase().slice(1)}`;
                rules.push(distributionRule);
            }

            return rules;
        }
    },

    // 色调偏离强度规则
    PARTITION_TONE_DEVIATION: {
        condition: (structuredConclusions, analysis) => {
            const rules = [];
            const statistics = analysis?.a?.statistics;

            if (statistics) {
                const toneDifference = Math.abs(statistics.mean - 128);
                if (toneDifference < 5) {
                    rules.push("deviationWeak");
                } else if (toneDifference < 15) {
                    rules.push("deviationModerate");
                } else {
                    rules.push("deviationStrong");
                }
            }

            return rules;
        }
    },

    // 总体结论生成规则
    // 基本色调特征
    BASIC_TONES: {
        condition: (global, _partitions) => {
            const tones = [];

            if (global.mainTone.tone === "GREEN" && global.mainTone.intensity === "STRONG") {
                tones.push("strongGreen");
            } else if (global.mainTone.tone === "RED" && global.mainTone.intensity === "STRONG") {
                tones.push("strongRed");
            } else if (global.mainTone.tone === "NEUTRAL" && global.mainTone.intensity === "STRONG") {
                tones.push("strongNeutral");
            } else if (global.mainTone.tone === "GREEN" && global.mainTone.intensity === "MODERATE") {
                tones.push("slightGreen");
            } else if (global.mainTone.tone === "RED" && global.mainTone.intensity === "MODERATE") {
                tones.push("slightRed");
            } else if (global.mainTone.tone === "NEUTRAL" && global.mainTone.intensity === "MODERATE") {
                tones.push("moderateNeutral");
            } else if (global.mainTone.tone === "GREEN" && global.mainTone.intensity === "WEAK") {
                tones.push("weakGreen");
            } else if (global.mainTone.tone === "RED" && global.mainTone.intensity === "WEAK") {
                tones.push("weakRed");
            } else if (global.mainTone.tone === "NEUTRAL" && global.mainTone.intensity === "WEAK") {
                tones.push("neutral");
            }

            return tones;
        }
    },

    // 色调分布特征
    DISTRIBUTION_FEATURES: {
        condition: (global, partitions) => {
            const distributions = [];

            if (global.mainTone.tone.includes("GREEN") &&
                partitions.blacks?.mainTone?.tone?.includes("GREEN") &&
                partitions.midtones?.mainTone?.tone?.includes("GREEN")) {
                distributions.push("consistentGreen");
            }
            if (global.mainTone.tone.includes("RED") &&
                partitions.blacks?.mainTone?.tone?.includes("RED") &&
                partitions.midtones?.mainTone?.tone?.includes("RED")) {
                distributions.push("consistentRed");
            }
            if (global.mainTone.tone.includes("GREEN") &&
                (partitions.blacks?.mainTone?.tone?.includes("RED") || partitions.shadows?.mainTone?.tone?.includes("RED"))) {
                distributions.push("greenWithWarmShadows");
            }
            if (global.mainTone.tone.includes("RED") &&
                (partitions.highlights?.mainTone?.tone?.includes("GREEN") || partitions.whites?.mainTone?.tone?.includes("GREEN"))) {
                distributions.push("redWithCoolHighlights");
            }
            if (global.mainTone.tone === "NEUTRAL" &&
                Object.values(partitions).some(p => p?.mainTone?.tone !== "NEUTRAL")) {
                distributions.push("neutralWithVariations");
            }

            return distributions;
        }
    },

    // 新增规则
    // 全局-分区一致性规则
    OVERALL_CONSISTENCY: {
        condition: (globalTone, partitionsData) => {
            if (!globalTone?.mainTone?.tone) return false;

            const { blacks, shadows, midtones, highlights, whites } = partitionsData;
            const partitions = [blacks, shadows, midtones, highlights, whites].filter(Boolean);

            let consistentCount = 0;
            partitions.forEach(partition => {
                if (partition?.mainTone?.tone === globalTone.mainTone.tone &&
                    partition.mainTone.intensity === globalTone.mainTone.intensity) {
                    consistentCount++;
                }
            });

            return consistentCount >= 3;
        }
    },

    // 全局-分区差异性规则
    OVERALL_VARIATIONS: {
        condition: (globalTone, partitionsData) => {
            if (!globalTone?.mainTone?.tone) return null;

            const { blacks, shadows, midtones, highlights, whites } = partitionsData;
            const partitions = [
                { name: 'blacks', data: blacks },
                { name: 'shadows', data: shadows },
                { name: 'midtones', data: midtones },
                { name: 'highlights', data: highlights },
                { name: 'whites', data: whites }
            ].filter(p => p.data);

            // 判断色调类型的辅助函数
            const getToneType = (tone) => {
                if (!tone) return null;
                if (tone === 'NEUTRAL') return 'NEUTRAL';
                if (tone.includes('RED')) return 'RED';
                if (tone.includes('GREEN')) return 'GREEN';
                return null;
            };

            const globalToneType = getToneType(globalTone.mainTone.tone);

            // 收集与全局色调不一致的分区
            const divergentPartitions = partitions
                .filter(partition => {
                    const mainTone = partition.data?.mainTone;
                    if (!mainTone?.tone) return false;

                    const partitionToneType = getToneType(mainTone.tone);
                    // 检查色调类型是否与全局不同
                    return partitionToneType !== globalToneType;
                })
                .map(partition => ({
                    name: partition.name,
                    tone: partition.data.mainTone.tone,
                    intensity: partition.data.mainTone.intensity
                }));


            return divergentPartitions.length >= 1 ? divergentPartitions : null;
        }
    },

    // 明暗分区对比规则
    SHADOW_HIGHLIGHT_CONTRAST: {
        condition: (globalTone, partitionsData) => {
            const { shadows, highlights } = partitionsData;
            if (!shadows?.mainTone || !highlights?.mainTone) return false;

            // 检查色调对比
            const isColorContrast = (
                (shadows.mainTone.tone.includes('RED') && highlights.mainTone.tone.includes('GREEN')) ||
                (shadows.mainTone.tone.includes('GREEN') && highlights.mainTone.tone.includes('RED'))
            );

            return isColorContrast;
        }
    }
};